import api from "../../api/axios";
import type { MinPriceSourceData, OtaScopeHotel } from "./types";

export type ApiFilterOption = { key: string; value: string };
export type ApiHotelFilterOption = {
  hotel_id: string | number;
  hotel_name: string;
  is_base: boolean | 0 | 1 | "0" | "1";
};

type FilterMap = Record<string, string>;

export type FiltersResponse = {
  category?: Array<ApiFilterOption | string> | FilterMap;
  ota?: Array<ApiFilterOption | string> | FilterMap;
  board_type?: Array<ApiFilterOption | string> | FilterMap;
  refund_policy?: Array<ApiFilterOption | string> | FilterMap;
  payment_type?: Array<ApiFilterOption | string> | FilterMap;
  check_in_dates?: { from: string; to: string } | null;
  hotel?: Array<ApiFilterOption | ApiHotelFilterOption | string> | FilterMap;
  data_source?: Array<ApiFilterOption | string> | FilterMap;
  room?: Array<ApiFilterOption | string> | FilterMap;
};

export type MetricsRequest = {
  group_id: number;
  category?: string[];
  ota?: string[];
  board_type?: string[];
  refund_policy?: string[];
  payment_type?: string[];
  date?: string;
  hotel?: string[];
  days_stay?: number;
  guests?: number;
  guests_child?: number[];
  date_from?: string;
  date_to?: string;
  day_type?: string;
};

export type MetricsItem = {
  date_from: string;
  client_avg: number;
  competitors_avg: number;
  difference: number;
  client_min?: number | null;
  competitors_min?: number | null;
  difference_min?: number | null;
  client_max?: number | null;
  competitors_max?: number | null;
  difference_max?: number | null;
  client_min_data?: MinPriceSourceData | null;
  competitor_min_data?: MinPriceSourceData | null;
};

export type MetricsSummary = {
  delta_avg_value?: number | null;
  delta_avg_percent?: number | null;
  client_avg_value?: number | null;
  competitors_avg_value?: number | null;
  days_competitor_higher?: number | null;
  days_competitor_lower?: number | null;
};

export type MetricsResponse = {
  summary?: MetricsSummary | null;
  items: MetricsItem[];
};

export type PriceOnDateRequest = {
  group_id: number;
  date: string;
  request_date: string;
  category?: string[];
  ota?: string[];
  board_type?: string[];
  refund_policy?: string[];
  payment_type?: string[];
  data_source?: string[];
  room?: string[];
  hotel?: string[];
  is_avg?: boolean;
  days_stay?: number;
  guests?: number;
  guests_child?: number[];
};

export type PriceOnDateItem = {
  hotel_id: string | number;
  hotel_name: string;
  room_name: string;
  is_base: boolean | 0 | 1 | "0" | "1";
  category: string;
  prices: Record<string, number | null>;
};

export async function getFilters(groupId: number) {
  const { data } = await api.get<FiltersResponse>("/api/v1/filters", {
    params: { group_id: groupId },
  });
  return data;
}

export type LastParsingDateRequest = {
  group_id: number;
  days_stay?: number;
};

export type LastParsingDateResponse = {
  date: string;
};

export async function getLastParsingDate(payload: LastParsingDateRequest) {
  const { data } = await api.post<LastParsingDateResponse>("/api/v1/get_last_parsing_date", payload);
  return data;
}

export type AvailableParsingDaysRequest = {
  group_id: number;
  min_stay: number;
  guests?: number;
  guests_child?: number[];
};

export type AvailableParsingDaysResponse = {
  status: "success" | "error";
  data: string[];
  error: unknown | null;
};

export async function getAvailableParsingDays(payload: AvailableParsingDaysRequest) {
  const params: Record<string, string | number | number[]> = {
    group_id: payload.group_id,
    min_stay: payload.min_stay,
    guests: payload.guests ?? 2,
  };

  if (payload.guests_child?.length) {
    params.guests_child = payload.guests_child;
  }

  const { data } = await api.get<AvailableParsingDaysResponse>("/api/v1/parsing/have_days", {
    params,
    paramsSerializer: {
      serialize: (queryParams) => {
        const searchParams = new URLSearchParams();

        Object.entries(queryParams).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach((item) => searchParams.append(key, String(item)));
            return;
          }
          if (value !== undefined && value !== null && value !== "") {
            searchParams.append(key, String(value));
          }
        });

        return searchParams.toString();
      },
    },
  });
  if (data.status !== "success") {
    throw new Error("Failed to load available parsing dates");
  }
  return data.data ?? [];
}

export async function getMetrics(payload: MetricsRequest) {
  const { data } = await api.post<MetricsResponse>("/api/v1/dashboard/price_position", payload);
  return data;
}

export async function downloadPricePositionXlsx(payload: MetricsRequest) {
  const response = await api.post<Blob>("/api/v1/dashboard/price_position/xlsx", payload, {
    responseType: "blob",
  });

  return {
    blob: response.data,
    filename: parseFilenameFromDisposition(response.headers["content-disposition"]),
  };
}

export async function getPriceOnDate(payload: PriceOnDateRequest) {
  const { data } = await api.post<PriceOnDateItem[]>("/api/v1/metrics/price_on_date", payload);
  return data;
}

export type PriceDynamicsRequest = {
  group_id: number;
  category?: string[];
  ota?: string[];
  board_type?: string[];
  refund_policy?: string[];
  payment_type?: string[];
  date: string;
  date_interval: { from: string; to: string };
  is_avg?: boolean;
  hotel?: string[];
  days_stay?: number;
  guests?: number;
  guests_child?: number[];
};

export type PriceDynamicsResponse = {
  hotels: Array<{ hotel_id: string | number; hotel_name: string; is_base: 0 | 1 }>;
  dates: Record<string, Record<string, number | null>>;
};

export async function getPriceDynamics(payload: PriceDynamicsRequest) {
  const { data } = await api.post<PriceDynamicsResponse>("/api/v1/metrics/price_dynamics", payload);
  return data;
}

export type MinPriceWithDeltaRequest = {
  group_id: number;
  date_from?: string;
  date_to?: string;
  category?: string[];
  ota?: string[];
  board_type?: string[];
  refund_policy?: string[];
  payment_type?: string[];
  request_date: string;
  hotel?: string[];
  days_stay?: number;
  guests?: number;
  guests_child?: number[];
};

export type MinPriceWithDeltaResponse = {
  hotels: Array<{
    hotel_id: string | number;
    hotel_name: string;
    is_base: boolean | 0 | 1 | "0" | "1" | "true" | "false";
  }>;
  dates: Record<
    string,
    Array<{
      hotel_id: string | number;
      prices: {
        price_today?: number | null;
        price_past?: number | null;
        delta?: number | null;
      };
    }>
  >;
};

export async function getMinPriceWithDelta(payload: MinPriceWithDeltaRequest) {
  const { data } = await api.post<MinPriceWithDeltaResponse>("/api/v1/metrics/min_price_with_delta", payload);
  return data;
}

function parseFilenameFromDisposition(disposition?: string) {
  if (!disposition) return undefined;

  const utfMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utfMatch?.[1]) {
    try {
      return decodeURIComponent(utfMatch[1]);
    } catch {
      return utfMatch[1];
    }
  }

  const quotedMatch = disposition.match(/filename="([^"]+)"/i);
  if (quotedMatch?.[1]) {
    return quotedMatch[1];
  }

  const plainMatch = disposition.match(/filename=([^;]+)/i);
  if (plainMatch?.[1]) {
    return plainMatch[1].trim();
  }

  return undefined;
}

export async function downloadPriceWithDeltaXlsx(payload: MinPriceWithDeltaRequest) {
  const response = await api.post<Blob>("/api/v1/dashboard/price_with_delta/xlsx", payload, {
    responseType: "blob",
  });

  return {
    blob: response.data,
    filename: parseFilenameFromDisposition(response.headers["content-disposition"]),
  };
}

export type PriceHotelsRequest = {
  group_id: number;
  date_from?: string;
  date_to?: string;
  category?: string[];
  ota?: string[];
  board_type?: string[];
  refund_policy?: string[];
  payment_type?: string[];
  date?: string;
  hotel?: string[];
  is_avg?: boolean;
  days_stay?: number;
  guests?: number;
  guests_child?: number[];
};

export async function getPriceHotels(payload: PriceHotelsRequest) {
  const { data } = await api.post<OtaScopeHotel[]>("/api/v1/metrics/price_hotels", payload);
  return data;
}

export async function downloadPriceHotelsXlsx(payload: PriceHotelsRequest) {
  const response = await api.post<Blob>("/api/v1/dashboard/price_hotels/xlsx", payload, {
    responseType: "blob",
  });

  return {
    blob: response.data,
    filename: parseFilenameFromDisposition(response.headers["content-disposition"]),
  };
}
